import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { useSecureHeader } from '@contexts/secure-header';

type Setter = (content: ReactNode | null) => void;

/*
 * Synced after every render so the header follows data as it loads, which is
 * why the node must be memoized: a new one each render would loop. Cleared
 * on unmount.
 */
function useHeaderSlot(set: Setter, content: ReactNode) {
  useEffect(() => {
    set(content);
  });

  useEffect(() => () => set(null), []);
}

/**
 * Inject custom content into the SecureHeader's title area.
 *
 * Pass a memoized ReactNode (via useMemo) so the header only re-renders when
 * your data actually changes. Content is cleared automatically on unmount.
 *
 * @example
 * const headerContent = useMemo(() => <MyTitle data={data} />, [data]);
 * useHeaderContent(headerContent);
 */
export function useHeaderContent(content: ReactNode) {
  useHeaderSlot(useSecureHeader().setHeaderContent, content);
}

/** Next to the page title, e.g. a switch between the page's views. Memoize it. */
export function useHeaderAddon(content: ReactNode) {
  useHeaderSlot(useSecureHeader().setHeaderAddon, content);
}

/** At the end of the header, e.g. a record pager. Memoize it. */
export function useHeaderActions(content: ReactNode) {
  useHeaderSlot(useSecureHeader().setHeaderActions, content);
}
